import { Button, Stack, styled, Theme, Typography, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";
import { Position } from "../../../graphql/types";

import { CloudArrowDownIcon } from "@phosphor-icons/react/dist/csr/CloudArrowDown";
import { CloudArrowUpIcon } from "@phosphor-icons/react/dist/csr/CloudArrowUp";

import { IfPermission } from "../../../components";
import { UnsuspendTransferButton } from "../../../components/UnsuspendTransfer/UnsuspendTransferButton";
import Permissions from "../../../constants/permissions";
import AccentDanger from "../../../icons/AccentDanger.svg?react";
import AccentInfo from "../../../icons/AccentInfo.svg?react";
import AccentSuccess from "../../../icons/AccentSuccess.svg?react";
import Issues from "../../../icons/Issues.svg?react";
import { useNavigator } from "../../../lib/Hooks";
import { toTitleCase } from "../../../lib/Util";

const GridContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "2fr 1fr",
  borderRadius: "8px",
  border: `1px solid ${theme.palette.surfaceLight30.main}`,
  boxShadow: theme.mixins.shadowSurfaceLight10,
  ...theme.applyStyles("dark", {
    boxShadow: theme.mixins.shadowSurfaceDark10,
  }),
  backgroundColor: theme.palette.surfaceLight10.main,
  padding: "16px",
}));

const Badge = ({ status, theme }: { status: string; theme: Theme }) => {
  let backgroundColor;
  let IconComponent;
  const iconProps = {
    style: { marginLeft: 0, marginRight: 0 },
    color: theme.palette.text10.main,
    width: "18px",
    height: "18px",
    fill: "",
  };

  switch (status) {
    case "suspended":
      IconComponent = AccentDanger;
      iconProps.fill = theme.palette.accentDanger30.main;
      backgroundColor = theme.palette.accentDanger20.main;
      break;

    case "blocked":
      IconComponent = Issues;
      iconProps.fill = theme.palette.accentWarning30.main;
      backgroundColor = theme.palette.accentWarning20.main;
      break;

    case "notice":
      IconComponent = AccentInfo;
      iconProps.fill = theme.palette.accentInfo30.main;
      backgroundColor = theme.palette.accentInfo20.main;
      break;

    default:
      IconComponent = AccentSuccess;
      iconProps.fill = theme.palette.accentSuccess30.main;
      backgroundColor = theme.palette.accentSuccess20.main;
      break;
  }

  return (
    <Stack
      direction="row"
      sx={{
        gap: "8px",
        alignItems: "center",
        padding: "4px 16px",
        backgroundColor,
        width: "fit-content",
        borderRadius: "35px",
        height: "26px",
      }}
    >
      <IconComponent {...iconProps} />
      <Typography variant="button.small" sx={{ color: "text50.main" }}>
        {toTitleCase(status)}
      </Typography>
    </Stack>
  );
};

const CardInfo = ({
  name,
  status,
  type,
  position,
  scriptbookId,
  refreshData,
  refreshing,
}: {
  name: string;
  status: string;
  type: "download" | "upload";
  scriptbookId: string;
  position?: Position;
  refreshData: () => Promise<void>;
  refreshing: boolean;
}) => {
  const navigator = useNavigator();
  const location = useLocation();
  const theme = useTheme();
  const icon = type === "upload" ? <CloudArrowUpIcon size="24px" /> : <CloudArrowDownIcon size="24px" />;

  const handleClickOpenInEditor = () => {
    navigator.goto(`scripts/editor/${scriptbookId}`, {
      mode: "Files",
      file: encodeURIComponent(position?.path || ""),
      variant: "editor",
      lines: `${position?.startLine}-${position?.endLine}-${position?.startColumn}-${position?.endColumn}`,
      referrer: `${location.pathname}?name=${name}&type=${type}`,
    });
  };

  const handleClickViewGraph = () => {
    navigator.goto("flows", { name, type });
  };

  return (
    <GridContainer>
      <Stack sx={{ gap: "16px" }}>
        <Badge status={status} theme={theme} />
        <Stack direction="row" sx={{ gap: "10px", alignItems: "center" }}>
          {icon}
          <Typography
            variant="body.large.semibold"
            sx={{
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {name}
          </Typography>
        </Stack>
      </Stack>
      <Stack sx={{ gap: "16px", alignItems: "flex-end" }}>
        {status === "suspended" && (
          <IfPermission permission={Permissions.DevelopScripts}>
            <UnsuspendTransferButton type={type} name={name} refreshData={refreshData} refreshing={refreshing} />
          </IfPermission>
        )}
        <IfPermission permission={Permissions.HiddenIntegrations}>
          <Button
            variant="outlined"
            color="primary"
            sx={{
              width: "fit-content",
              whiteSpace: "nowrap",
            }}
            onClick={handleClickViewGraph}
          >
            View Graph
          </Button>
        </IfPermission>
        <Button
          variant="outlined"
          color="primary"
          sx={{
            width: "fit-content",
            whiteSpace: "nowrap",
          }}
          onClick={handleClickOpenInEditor}
        >
          Open in Editor
        </Button>
      </Stack>
    </GridContainer>
  );
};

export { CardInfo };
